"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import type { StaticImageData } from "next/image";
import Link from "next/link";
import type { MouseEvent } from "react";
import { isDiscoveryJsCommandHref, linkTargetProps, runDiscoveryJsCommand, type DiscoveryFooterView, type DiscoveryNavLinkView } from "@repo/sanity-utils/discoveryChrome";
import { trackDiscoveryLinkClick } from "@repo/tracking/discovery/lib/discovery-link-tracking";
import { SocialNetworkIcon } from "./SocialNetworkIcon";
import { Typography } from "./typography";
import styles from "./DiscoveryFooterNav.module.css";
type Props = {
  /** Required when `isLoading` is not set. */
  data?: DiscoveryFooterView;
  fallbackLogoSrc: string | StaticImageData;
  fallbackLogoHref: string;
  /** Optional extra click handler after Discovery funnel tracking. */
  onNavLinkClick?: (href: string) => void;
  /**
   * Suspense fallback: same layout CSS as the loaded footer, but inert and
   * `aria-hidden` so the empty logo-only chrome doesn't jump the page.
   */
  isLoading?: boolean;
};
const FOOTER_SKELETON_SOCIAL = 5;
const FOOTER_SKELETON_COLUMNS = 3;
const FOOTER_SKELETON_LINKS = 4;
const FOOTER_SKELETON_LEGAL = 3;
function DiscoveryFooterNavSkeleton(t0) {
  const $ = _c(14);
  const {
    fallbackLogoSrc
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "auto",
      height: 48
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== fallbackLogoSrc) {
    t2 = <span className={styles.logoLink}><Image src={fallbackLogoSrc} alt="" width={215} height={48} className={styles.logo} style={t1} unoptimized={true} /></span>;
    $[1] = fallbackLogoSrc;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ul className={styles.socialList} role="presentation">{Array.from({
        length: FOOTER_SKELETON_SOCIAL
      }, _temp)}</ul>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <div className={styles.brand}>{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={styles.columns}>{Array.from({
        length: FOOTER_SKELETON_COLUMNS
      }, _temp3)}</div>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <div className={styles.top}>{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <hr className={styles.divider} />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Typography as="p" variant="bodySmall" className={styles.copyright}><span className={`${styles.skeletonBone} ${styles.skeletonCopyright}`} /></Typography>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={styles.bottom}>{t8}<ul className={styles.legalList} role="presentation">{Array.from({
          length: FOOTER_SKELETON_LEGAL
        }, _temp4)}</ul></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6) {
    t10 = <footer className={`${styles.footer} ${styles.chalkboardGreen} ${styles.loading}`} aria-hidden={true} aria-busy="true" inert={true}><div className={styles.inner}>{t6}{t7}{t9}</div></footer>;
    $[12] = t6;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
}
function _temp4(__2, index_0) {
  return <li key={index_0}><Typography as="span" variant="bodySmall"><span className={`${styles.skeletonBone} ${styles.skeletonLegal}`} /></Typography></li>;
}
function _temp3(__0, column) {
  return <div key={column} className={styles.column}><Typography as="p" variant="eyebrow" className={styles.columnHeading}><span className={`${styles.skeletonBone} ${styles.skeletonColumnHeading}`} /></Typography><ul className={styles.linkList} role="presentation">{Array.from({
        length: FOOTER_SKELETON_LINKS
      }, _temp2)}</ul></div>;
}
function _temp2(__1, link) {
  return <li key={link}><Typography as="span" variant="footerNav"><span className={`${styles.skeletonBone} ${styles.skeletonFooterLink}`} /></Typography></li>;
}
function _temp(_, index) {
  return <li key={index}><span className={styles.socialLink}><span className={`${styles.skeletonBone} ${styles.skeletonSocial}`} /></span></li>;
}
const COPYRIGHT_YEAR = new Date().getUTCFullYear();
export function DiscoveryFooterNav(t0) {
  const $ = _c(41);
  const {
    data,
    fallbackLogoSrc,
    fallbackLogoHref,
    onNavLinkClick,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  if (isLoading) {
    let t2;
    if ($[0] !== fallbackLogoSrc) {
      t2 = <DiscoveryFooterNavSkeleton fallbackLogoSrc={fallbackLogoSrc} />;
      $[0] = fallbackLogoSrc;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== data?.items) {
    t2 = data?.items ?? [];
    $[2] = data?.items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const items = t2;
  let t3;
  if ($[4] !== data?.socialLinks) {
    t3 = data?.socialLinks ?? [];
    $[4] = data?.socialLinks;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const socialLinks = t3;
  let t4;
  if ($[6] !== data?.legalLinks) {
    t4 = data?.legalLinks ?? [];
    $[6] = data?.legalLinks;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const legalLinks = t4;
  const showLogo = data?.showLogo !== false;
  const copyrightText = data?.copyrightText ?? "";
  const styleClass = data?.style === "subtlePaper" ? styles.subtlePaper : styles.chalkboardGreen;
  const logoHref = data?.logoHref || fallbackLogoHref;
  const logoAlt = data?.logoAlt || "Art of Problem Solving";
  const logoSrc = data?.logoUrl || fallbackLogoSrc;
  let t5;
  if ($[8] !== onNavLinkClick) {
    const reportNavLinkClick = function reportNavLinkClick(href) {
      trackDiscoveryLinkClick(href, "footer_nav");
      onNavLinkClick?.(href);
    };
    const handleFooterLinkClick = function handleFooterLinkClick(event, link, options) {
      if (isDiscoveryJsCommandHref(link.href)) {
        event.preventDefault();
        runDiscoveryJsCommand(link.href);
        return;
      }
      if (options?.track) {
        reportNavLinkClick(link.href);
      }
    };
    t5 = function footerLinkProps(link_0, options_0) {
      const isJsCommand = isDiscoveryJsCommandHref(link_0.href);
      return {
        href: isJsCommand ? "#" : link_0.href,
        ...(isJsCommand ? {
          role: "button" as const
        } : linkTargetProps(link_0.openInNewTab)),
        onClick: event_0 => handleFooterLinkClick(event_0, link_0, options_0)
      };
    };
    $[8] = onNavLinkClick;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const footerLinkProps = t5;
  const t6 = `${styles.footer} ${styleClass}`;
  let t7;
  if ($[10] !== logoAlt || $[11] !== logoHref || $[12] !== logoSrc || $[13] !== showLogo) {
    t7 = showLogo && <Link href={logoHref} className={styles.logoLink}><Image src={logoSrc} alt={logoAlt} width={215} height={48} className={styles.logo} style={{
        width: "auto",
        height: 48
      }} unoptimized={true} /></Link>;
    $[10] = logoAlt;
    $[11] = logoHref;
    $[12] = logoSrc;
    $[13] = showLogo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== socialLinks) {
    t8 = socialLinks.length > 0 && <ul className={styles.socialList} role="list">{socialLinks.map(_temp5)}</ul>;
    $[15] = socialLinks;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className={styles.brand}>{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== footerLinkProps || $[21] !== items) {
    t10 = items.length > 0 && <div className={styles.columns}>{items.map(column => <div key={column._key} className={styles.column}><Typography as="p" variant="eyebrow" className={styles.columnHeading}>{column.label}</Typography><ul className={styles.linkList} role="list">{column.children.map(link_2 => <li key={link_2._key}><a className={styles.link} {...footerLinkProps(link_2, {
              track: true
            })}><Typography as="span" variant="footerNav">{link_2.label}</Typography></a></li>)}</ul></div>)}</div>;
    $[20] = footerLinkProps;
    $[21] = items;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className={styles.top}>{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <hr className={styles.divider} />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== copyrightText) {
    t13 = <Typography as="p" variant="bodySmall" className={styles.copyright}>© {COPYRIGHT_YEAR} {copyrightText}</Typography>;
    $[27] = copyrightText;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== footerLinkProps || $[30] !== legalLinks) {
    t14 = legalLinks.length > 0 && <ul className={styles.legalList} role="list">{legalLinks.map(link_3 => <li key={link_3._key}><a className={styles.legalLink} {...footerLinkProps(link_3)}><Typography as="span" variant="bodySmall">{link_3.label}</Typography></a></li>)}</ul>;
    $[29] = footerLinkProps;
    $[30] = legalLinks;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <div className={styles.bottom}>{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15) {
    t16 = <div className={styles.inner}>{t11}{t12}{t15}</div>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== t6) {
    t17 = <footer className={t6} aria-label="Site footer">{t16}</footer>;
    $[38] = t16;
    $[39] = t6;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
function _temp5(link_1) {
  return <li key={link_1._key}><a href={link_1.url} aria-label={link_1.label} className={styles.socialLink} target="_blank" rel="noopener noreferrer"><SocialNetworkIcon network={link_1.key} className={styles.socialIcon} size={24} /></a></li>;
}